import React, { ChangeEvent, FC, ReactElement } from 'react';
import { NavigateFunction, useNavigate } from 'react-router';
import { useFormik } from 'formik';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';

import FormGroup from '../FormGroup';
import PrimaryButton from '../PrimaryButton';
import { fetchFromRestAPI } from '../../util/api';
import i18next from 'i18next';
import { AUTH0_ORGANIZATION_ID } from '../../config';

const verifyRoom = async (roomCode: string): Promise<boolean | undefined> => {
  return fetchFromRestAPI(`/api/v1/room/verify/${roomCode}?tenant=${AUTH0_ORGANIZATION_ID}`);
};

const PatientRoomCodeBox: FC = (): ReactElement => {
  const navigate: NavigateFunction = useNavigate();

  const formik = useFormik({
    initialValues: {
      roomCode: '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      
      if (!values.roomCode) {
        errors.roomCode = i18next.t('loginPage.joinRoomEmptyError');
      } else if (values.roomCode.length < 4) {
        errors.roomCode = i18next.t('loginPage.joinRoomLengthError');
      }
      return errors;
    },
    onSubmit: async (values, actions) => {
      try {
        const isVerified: boolean | undefined = await verifyRoom(values.roomCode);

        if (isVerified && isVerified) {
          navigate(`/call/${values.roomCode}`);
          return;
        }

        actions.setFieldError(
          'roomCode', 
          i18next.t('loginPage.notVerifiedRoomError', { code: values.roomCode })
        );
      } catch (_) {
        actions.setFieldError(
          'roomCode',
          i18next.t('loginPage.notVerifiedRoomError', { code: values.roomCode })
        );
      } finally {
        actions.setSubmitting(false);
      }
    },
  });
  
  const handleRoomCodeChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const uppercaseValue: string = event.target.value.toUpperCase();
    formik.setFieldValue('roomCode', uppercaseValue);
  }
  
  return (
    <>
      <div className="mb-3">
        {i18next.t('loginPage.joinRoomDescription')}
      </div>
      
      <form onSubmit={formik.handleSubmit}>
        <FormGroup as={Col}>
          <Form.Label column={false}>
            {i18next.t('loginPage.joinRoomHeading')}
          </Form.Label>

          {formik.errors.roomCode && (
            <div className="fw-bold fs-6 text-danger mb-2">
              {formik.errors.roomCode}
            </div>
          )}
          
          <Form.Control
            placeholder="XXXX"
            value={formik.values.roomCode}
            id="roomCode"
            name="roomCode"
            onChange={handleRoomCodeChange}
            onBlur={formik.handleBlur}
            isInvalid={!!formik.touched.roomCode && !!formik.errors.roomCode}
            autoCapitalize="characters"
            autoCorrect="off"
            style={{
              letterSpacing: '0.5rem',
              textTransform: 'uppercase',
            }}
          />
        </FormGroup>
        
        <PrimaryButton
          type="submit"
          disabled={!formik.isValid || formik.isSubmitting}
          className="w-100"
        >
          {i18next.t('loginPage.joinRoomButton')}
        </PrimaryButton>
      </form>
      
      <div className="text-center fs-5 mt-3">
        <p dangerouslySetInnerHTML={{ __html: i18next.t('loginPage.joinRoomSupport') }} />
      </div>
    </>
  )
};

export default PatientRoomCodeBox;
